import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ChatList from '../components/chat/ChatList';
import ChatWindow from '../components/chat/ChatWindow';
import { Input } from '../components/ui/input';
import { Headset } from '@phosphor-icons/react';
import { DISAPPEARING_DURATIONS } from '../lib/constants';
import InvitationNotice from '../components/sworn/InvitationNotice';
import { Frame } from '../components/shell/AppShell';
import PageHeader from '../components/shell/PageHeader';
import supportArt from '../assets/bg/private-support.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedChat, setSelectedChat] = useState(null);
  // Queue view state, held here so it survives opening a conversation.
  const [queueFilter, setQueueFilter] = useState('all');
  const [queueQuery, setQueueQuery] = useState('');

  const isUser = user?.role === 'user';
  const location = useLocation();

  // Room's Secure Capsule door arrives with { capsule, from } in router state.
  // Read once, then cleared from history, so Back or a reload does not open
  // compose again. The origin is kept only in memory: after a reload it is
  // gone and Cancel simply leaves the member in Support (approved in 2.0).
  const [capsuleIntent] = useState(() => !!location.state?.capsule);
  const [capsuleOrigin] = useState(() => location.state?.from || null);
  useEffect(() => {
    if (location.state?.capsule) navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  // Normal users have exactly one support thread. Auto-open it.
  useEffect(() => {
    if (!isUser || selectedChat) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/chats`, { withCredentials: true });
        if (!cancelled && data.length > 0) setSelectedChat(data[0]);
      } catch (e) { /* no-op */ }
    })();
    return () => { cancelled = true; };
  }, [isUser, selectedChat]);

  const handleToggleDisappearing = async (chat) => {
    if (user?.role !== 'superadmin') {
      alert('Only superadmin can change disappearing-messages settings');
      return;
    }
    const enabled = !chat.disappearing_messages_enabled;
    let durationSeconds = null;
    if (enabled) {
      const choice = window.prompt('Disappearing duration: enter 1h, 24h, or 7d', '24h');
      if (!choice) return;
      durationSeconds = DISAPPEARING_DURATIONS[choice.trim().toLowerCase()];
      if (!durationSeconds) {
        alert('Invalid duration. Use 1h, 24h, or 7d.');
        return;
      }
    }
    try {
      const params = new URLSearchParams({ enabled: String(enabled) });
      if (durationSeconds) params.set('duration_seconds', String(durationSeconds));
      await axios.patch(
        `${API_URL}/api/chats/${chat.id}/disappearing?${params.toString()}`,
        {},
        { withCredentials: true }
      );
      setSelectedChat({
        ...chat,
        disappearing_messages_enabled: enabled,
        disappearing_duration_seconds: durationSeconds,
      });
    } catch (e) {
      alert('Failed to update disappearing messages setting');
    }
  };

  // ---------- Member view: Private Support, inside the 2.0 shell ----------
  // One thread, no list. Everything the account menu used to hold now lives
  // under More, and the Sworn invitation notice under Room.
  if (isUser) {
    return (
      <Frame user={user} background={supportArt} activeKey="support" fill>
        {/* A readable column on a wide screen, as the Commons and the Sworn
            have; on a phone it is simply the whole width. */}
        <div className="mx-auto w-full max-w-2xl flex-1 min-h-0 flex flex-col" data-testid="dashboard">
          <PageHeader title="Private Support" subtitle="Private line to the Society.">
            {/* Said once, quietly, where the member writes. The fuller
                disclosure stays on the gate, the invitation and the
                Society's documents. */}
            <p className="mx-auto mt-2 max-w-sm text-[11px] leading-snug text-muted-foreground" data-testid="support-disclosure">
              Ordinary messages here can be read by authorised Society staff.
              They are not end-to-end encrypted.
            </p>
          </PageHeader>
          {selectedChat ? (
            <ChatWindow
              chat={selectedChat}
              onBack={() => {}}
              onToggleDisappearing={handleToggleDisappearing}
              inShell
              capsuleAutoOpen={capsuleIntent}
              onCapsuleCancel={capsuleOrigin ? () => navigate(capsuleOrigin) : undefined}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div>
                <Headset className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">
                  Your support thread is being prepared. Please refresh in a moment.
                </p>
              </div>
            </div>
          )}
        </div>
      </Frame>
    );
  }

  // ---------- Staff view: Support Queue, inside the 2.0 shell ----------
  // List and conversation side by side from md up; on a phone the
  // conversation is a child screen, and Back returns to the list with its
  // filter and search exactly as they were (they live here, not in the list).
  return (
    <Frame user={user} background={supportArt} scrim={0.5} activeKey="queue" fill>
      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12" data-testid="dashboard">
        <div
          className={`
            col-span-12 md:col-span-4 lg:col-span-3 min-h-0
            flex-col border-r border-border bg-background/85 backdrop-blur-sm
            ${selectedChat ? 'hidden md:flex' : 'flex'}
          `}
        >
          <PageHeader title="Support Queue">
            {/* Said to staff where they read: the other half of what members
                are told in their own thread. */}
            <p className="mx-auto mt-2 max-w-sm text-[11px] leading-snug text-muted-foreground" data-testid="staff-access-notice">
              You can read the conversations you have access to; they are not
              end-to-end encrypted.{' '}
              {user?.role === 'superadmin'
                ? 'You see every member support thread.'
                : 'You see only the members assigned to you.'}
            </p>
          </PageHeader>

          <div className="px-3 pb-3 space-y-2">
            <div className="flex gap-1 rounded-xl border border-border bg-card/80 p-1" role="tablist" aria-label="Filter">
              {[['all', 'All'], ['unread', 'Unread']].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={queueFilter === id}
                  onClick={() => setQueueFilter(id)}
                  className={[
                    'min-h-[40px] flex-1 rounded-lg text-sm transition-colors',
                    queueFilter === id ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground',
                  ].join(' ')}
                  data-testid={`queue-filter-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <Input
              value={queueQuery}
              onChange={(e) => setQueueQuery(e.target.value)}
              inputMode="numeric"
              placeholder="Find by member number"
              aria-label="Find by member number"
              className="h-10"
              data-testid="queue-search"
            />
          </div>

          <InvitationNotice />
          <ChatList
            onSelectChat={setSelectedChat}
            selectedChatId={selectedChat?.id}
            filter={queueFilter}
            query={queueQuery}
          />
        </div>

        <div
          className={`
            col-span-12 md:col-span-8 lg:col-span-9
            flex-col min-h-0 overflow-hidden
            ${selectedChat ? 'flex' : 'hidden md:flex'}
          `}
        >
          <ChatWindow
            chat={selectedChat}
            onBack={() => setSelectedChat(null)}
            onToggleDisappearing={handleToggleDisappearing}
            staffInShell
          />
        </div>
      </div>
    </Frame>
  );
};

export default Dashboard;
